import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Calendar } from "lucide-react";
import { toast } from "sonner";
import apiClient from "@/lib/apiClient";

interface BirthDateDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const BirthDateDialog: React.FC<BirthDateDialogProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [year, setYear] = useState<string>("");
  const [month, setMonth] = useState<string>("");
  const [day, setDay] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);

  const handleSubmit = async () => {
    // バリデーション
    if (!year || !month || !day) {
      toast.error("生年月日を入力してください");
      return;
    }

    const yearNum = parseInt(year, 10);
    const monthNum = parseInt(month, 10);
    const dayNum = parseInt(day, 10);

    // 基本的な範囲チェック
    if (yearNum < 1900 || yearNum > new Date().getFullYear()) {
      toast.error("有効な年を入力してください");
      return;
    }

    if (monthNum < 1 || monthNum > 12) {
      toast.error("有効な月を入力してください（1-12）");
      return;
    }

    if (dayNum < 1 || dayNum > 31) {
      toast.error("有効な日を入力してください（1-31）");
      return;
    }

    // YYYY-MM-DD 形式に変換
    const birthDate = `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;

    setLoading(true);
    try {
      await apiClient.put("/me", { birth_date: birthDate });
      toast.success("生年月日を登録しました");
      onSuccess();
    } catch (error: any) {
      if (error.response?.data?.error) {
        toast.error(error.response.data.error);
      } else {
        toast.error("生年月日の登録に失敗しました");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-blue-500" />
            生年月日の登録
          </DialogTitle>
          <DialogDescription>
            ポイント購入には年齢確認が必要です。生年月日を入力してください。
            <br />
            <span className="text-red-600 font-medium">
              ※18歳未満の方は利用できません
            </span>
            <br />
            <span className="text-gray-500 text-sm">
              ※一度登録すると変更できません
            </span>
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          <div className="grid grid-cols-3 gap-2">
            <div className="space-y-2">
              <Label htmlFor="year">年</Label>
              <Input
                id="year"
                type="number"
                placeholder="1990"
                value={year}
                onChange={(e) => setYear(e.target.value)}
                min="1900"
                max={new Date().getFullYear()}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="month">月</Label>
              <Input
                id="month"
                type="number"
                placeholder="1"
                value={month}
                onChange={(e) => setMonth(e.target.value)}
                min="1"
                max="12"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="day">日</Label>
              <Input
                id="day"
                type="number"
                placeholder="1"
                value={day}
                onChange={(e) => setDay(e.target.value)}
                min="1"
                max="31"
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            disabled={loading}
          >
            キャンセル
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? "登録中..." : "登録する"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BirthDateDialog;
